import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AnalyticsService } from '../../analytics/analytics.service';
import { PageTypes } from '../../analytics/enums/pageTypes';
import { EFeatureToggles, EGlueResource, EStoreFeatures, EStoreTypes, EUserRoles } from '../../configurations/common';
import { CheckoutFacade } from '../../facades/checkout.facade';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { CustomerFacade } from '../../facades/customer.facade';
import { MarketingFacade } from '../../facades/marketing.facade';
import { ICartRule } from '../../models/cart.models';
import { ICheckoutUpdate } from '../../models/checkout.models';
import { IAddress } from '../../models/common.models';
import { State } from '../../reducers';
import * as ShopCartSelectors from '../../reducers/shop-cart.reducer';
import { I18nService } from '../../services';
import { AddressUtils } from '../../utils/address.utils';
import { AppUtils } from '../../utils/app.utils';
import { FileType, FileUtils } from '../../utils/file.utils';
import { ObjectUtils } from '../../utils/object.utils';
import { HttpResponse } from '@angular/common/http';
import { IAddressReduced, IAddressSelectBox } from '../../models/customer.models';

@Component({
  selector: 'app-delivery-details',
  templateUrl: './delivery-details.component.html',
  standalone: false,
})
export class DeliveryDetailsComponent implements OnInit, OnDestroy {
  private readonly router: Router = inject(Router);
  private readonly checkoutFacade: CheckoutFacade = inject(CheckoutFacade);
  private readonly store: Store<State> = inject<Store<State>>(Store);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly i18nService: I18nService = inject(I18nService);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  userRoles!: EUserRoles[];
  isUserRolesLoading: boolean = true;
  hasApproverAccess: boolean = false;
  isCheckoutQuoteApprovalMandatory: boolean = false;
  isJpStore: boolean = false;
  isAuStore: boolean = false;
  customerAddresses: IAddress[] = [];
  addresses: IAddressSelectBox[] = [];
  businessAddresses: IAddress[] | IAddressReduced[] = [];
  creatingOrderInProgress: boolean = false;
  creatingPreviewInProgress: boolean = false;
  cartItems: any;
  isCartEmpty: boolean = true;
  cartId!: string;
  showApprovalModal: boolean = false;
  cartRules!: Array<ICartRule>;
  inProcessCartId!: string;
  businessAddressesLoading: boolean = false;
  isRfqFlowEnabled: boolean = false;
  itemWithoutPrice: boolean = false;
  excludeTax: boolean = false;
  successModalMessage: string = '';

  isOnContactDetailsSection: boolean = false;
  orderDetailsData: any;
  contactDetailsData: any;
  cartItemsWithDetails: any;
  isSdmEnabled: boolean = false;
  billingAddress: IAddress | null = null;
  soldToAddress: IAddress | null = null;
  payerAddress: IAddress | null = null;

  private unsubscribe$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.isJpStore = AppUtils.isStoreActive(EStoreTypes.JP);
    this.isAuStore = AppUtils.isStoreActive(EStoreTypes.AU);
    this._selectIsRfqFlowEnabled();

    this.selectCustomerCompanyRoles();
    this.selectCompanyRolesLoading();
    this.selectIsCartEmpty();
    this.loadCartData();
    this.customerFacade.beginGetBusinessUnitsAction();
    this.selectCustomerAddresses();
    this.selectBusinessAddresses();
    this.selectCartItemsSubscription();
    this.selectExcludeTax();
    this.selectIsQuoteApprovalForCheckoutRequired();
    this._setIsSdmEnabled();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  selectCustomerCompanyRoles(): void {
    this.customerFacade.getCustomerCompanyRoles().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(roles => {
      this.userRoles = roles;
      this.hasApproverAccess = this.checkRolesForAccess(roles);
    });
  }

  selectCompanyRolesLoading(): void {
    this.customerFacade.selectCompanyRolesLoading().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isRolesLoading => {
      this.isUserRolesLoading = isRolesLoading;
    });
  }

  selectIsCartEmpty(): void {
    this.marketingFacade.selectIsCartEmpty().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.isCartEmpty = state;
    });
  }

  checkRolesForAccess(roles: EUserRoles[]): boolean {
    if (roles.includes(EUserRoles.Viewer) && roles.length === 1) {
      this.router.navigate([this.i18nService.getCurrentLocale()]);
    }

    return (roles.includes(EUserRoles.Approver) || roles.includes(EUserRoles.Admin));
  }

  selectBusinessAddresses(): void {
    this.businessAddressesLoading = true;
    this.customerFacade.selectBusinessAddress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(addresses => {
      if (addresses) {
        this.businessAddresses = addresses;
        AddressUtils.appendAddresses(this.addresses, this.businessAddresses, 'business');
        this.businessAddressesLoading = false;
      }
    });
  }

  selectCustomerAddresses(): void {
    this.customerFacade.selectCustomerAddresses().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(addresses => {
      if (addresses) {
        this.customerAddresses = addresses;
        AddressUtils.appendAddresses(this.addresses, this.customerAddresses, 'customer');
      }
    });
  }

  createOrder(contactDetailsData: any): void {
    this.contactDetailsData = contactDetailsData;

    const checkoutUpdate: ICheckoutUpdate = {
      data: {
        type: EGlueResource.CHECKOUT_UPDATE,
        attributes: {
          idCart: this.cartId,
          isAddressSavingSkipped: true,
          systemDetails: {
            ...this.orderDetailsData.systemDetails,
          },
          systemDetailsPerItem: [
            ...this.orderDetailsData.systemDetailsPerItem,
          ],
          pointOfContact: {
            ...this.contactDetailsData.pointOfContact,
          },
          approverDetails: {
            approverId: this.contactDetailsData.approverData.approver.value,
            comment: this.contactDetailsData.approverData.commentForApprover,
            dueDate: null,
            firstName: this.contactDetailsData.approverData.approver.firstName,
            lastName: this.contactDetailsData.approverData.approver.lastName,
          },
          shippingAddress: this.getShippingAddress(),
          billingAddress: this.billingAddress,
          soldToAddress: this.soldToAddress,
          payerAddress: this.payerAddress,
        },
      },
    };

    this.creatingOrderInProgress = true;
    this.checkoutFacade.postCheckoutUpdate(checkoutUpdate).pipe(
      take(1),
    ).subscribe({
      next: _ => {
        this.generateQuotePdf();
        this.creatingOrderInProgress = false;
        this.showApprovalModal = true;
        this.generateSuccessModalMessage();

        if (this.userRoles.includes(EUserRoles.Buyer) || this.isCheckoutQuoteApprovalMandatory) {
          this.analyticsService.trackCart('order.placed');
          this.inProcessCartId = this.cartId;
          // TODO: logic duplication - move to one effect
          this.marketingFacade.createEmptyCart();
          return;
        }

        this.checkoutFacade.actionPutCartIdOrderApprove(this.inProcessCartId);
        this.router.navigate(['/order-approval/', this.cartId]).then();
      },
      error: _ => {
        this.creatingOrderInProgress = false;
      },
    });
  }

  getPreviewQuotePdf(): void {
    this.creatingPreviewInProgress = true;
    this.checkoutFacade.postCheckoutUpdate({
      data: {
        type: EGlueResource.CHECKOUT_UPDATE,
        attributes: {
          idCart: this.cartId,
          shippingAddress: this.getShippingAddress(),
          billingAddress: this.billingAddress,
          soldToAddress: this.soldToAddress,
          payerAddress: this.payerAddress,
        },
      },
    }).pipe(
      take(1),
    ).subscribe({
      next: () => {
        this.checkoutFacade.getCartGenerateQuotePdfFile(this.cartId, true).pipe(
          take(1),
        ).subscribe({
          next: (response: HttpResponse<Blob>) => {
            const filename: string = FileUtils.extractFilenameFromContentDisposition(
              response.headers.get('Content-Disposition') ?? '',
              `PREVIEW_quote_${this.cartId}`,
            );
            if (response.body) {
              FileUtils.saveAndOpenFile(response.body, FileType.PDF, filename);
            }
          },
          complete: () => this.creatingPreviewInProgress = false,
          error: () => this.creatingPreviewInProgress = false,
        });
      },
      error: () => {
        this.creatingOrderInProgress = false;
      },
    });
  }

  backToHomepage(): void {
    this.router.navigate(
      [this.i18nService.getCurrentLocale()],
      {queryParams: {lastCartId: this.inProcessCartId}},
    ).then();
  }

  proceedToContactDetails(orderDetailsData: any): void {
    this.orderDetailsData = orderDetailsData;
    this.isOnContactDetailsSection = true;
  }

  proceedToOrderDetails(): void {
    this.isOnContactDetailsSection = false;
  }

  requestQuoteFeatureActive(): boolean {
    return this.isRfqFlowEnabled && this.itemWithoutPrice;
  }

  /**
   * Check if SDM is enabled and set the value to the variable
   *
   * @void
   * @private
   */
  private _setIsSdmEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.SDM_INTEGRATION).pipe(take(1)).subscribe(value => {
      this.isSdmEnabled = value;
    });
  }

  private getShippingAddress(): IAddress {
    const deliveryAddress: any = this.orderDetailsData.deliveryAddress;

    const shippingAddress: IAddress = {
      idCustomerAddress: deliveryAddress.idCustomerAddress,
      idCompanyUnitAddress: deliveryAddress.idCompanyUnitAddress,
      salutation: 'Ms',
      firstName: deliveryAddress?.firstName ? deliveryAddress.firstName : 'empty',
      lastName: deliveryAddress?.lastName ? deliveryAddress.lastName : 'empty',
      address1: deliveryAddress?.address1 ? deliveryAddress.address1 : 'empty',
      address2: deliveryAddress?.address2 ? deliveryAddress.address2 : 'empty',
      address3: '',
      zipCode: deliveryAddress?.zipCode ? deliveryAddress.zipCode : 'empty',
      city: deliveryAddress?.city ? deliveryAddress.city : 'empty',
      iso2Code: deliveryAddress?.iso2Code ? deliveryAddress.iso2Code : 'DE',
      company: deliveryAddress?.company ? deliveryAddress.company : 'empty',
      phone: deliveryAddress?.phone ? deliveryAddress.phone : 'empty',
      country: deliveryAddress?.country ? deliveryAddress.country : 'empty',
      isDefaultBilling: false,
      isDefaultShipping: false,
      isCustom: deliveryAddress?.isCustom ?? false,
    };

    // handle BU address iso2Code
    if (shippingAddress.country === 'empty') {
      shippingAddress.country = AddressUtils.getDeliveryCountryByIsoCode(shippingAddress.iso2Code)?.name || 'empty';
    }

    if (!this.isSdmEnabled) {
      this.billingAddress = shippingAddress;
      this.soldToAddress = shippingAddress;
      this.payerAddress = shippingAddress;
    } else {
      this.billingAddress = null;
      this.soldToAddress = null;
      this.payerAddress = null;
    }

    return shippingAddress;
  }

  private generateQuotePdf(): void {
    if (this.isJpStore && this.cartId) {
      this.checkoutFacade.getCartGenerateQuotePdfFile(this.cartId, false).pipe(take(1))
        .subscribe({
          next: (response: HttpResponse<Blob>) => {
            const filename: string = FileUtils.extractFilenameFromContentDisposition(
              response.headers.get('Content-Disposition') ?? '',
              `Generated_quote_${this.cartId}`,
            );
            if (response.body) {
              FileUtils.saveAndOpenFile(response.body, FileType.PDF, filename);
            }
          },
        });
    }
  }

  private selectCartItemsSubscription(): void {
    this.marketingFacade.selectCartItemsWithDetails().pipe(
      take(1),
    ).subscribe(cartItems => {
      if (cartItems && cartItems.length > 0 && !this.cartItemsWithDetails?.length) {
        this.cartItemsWithDetails = cartItems;
      }
    });
  }

  private selectExcludeTax(): void {
    this.excludeTax = this.configurationFacade.isFeatureAvailable(EStoreFeatures.EXCLUDE_TAX);
  }

  private loadCartData(): void {
    this.store.select(ShopCartSelectors.selectCartItems).pipe(
      take(1),
    ).subscribe(cartItems => {
      if (!cartItems) {
        return;
      }

      this.cartItems = ObjectUtils.deepClone(cartItems.data.attributes);
      this.cartRules = cartItems.included.filter((include: ICartRule) => include.type === 'cart-rules');
      this.analyticsService.setProducts(cartItems);
    });

    this.marketingFacade.selectCartId()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(cartId => {
        this.cartId = cartId ?? '';
        this.analyticsService.setCartId(this.cartId);
      });
    this.analyticsService.trackPageReady('delivery details', PageTypes.DELIVERY_DETAILS_PAGE, 'concrete-products');
  }


  /**
   * Generate message for modal that pops up after click on generate quote button. Approver role has URL for redirect
   * to order-approval page only when APPROVER_HYPERLINK feature toggle is enabled.
   */
  generateSuccessModalMessage(): void {
    let messageKey: string = 'checkout-delivery-details.approval-success-message';
    const messageUrl: string = this.i18nService.getCurrentLocale() + '/order-approval/' + this.cartId;
    let messageArgs: object = {};
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.APPROVER_HYPERLINK).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe((value: boolean) => {
      if (value && this.userRoles.includes(EUserRoles.Approver)) {
        messageKey = 'checkout-delivery-details.approval-success-message-approver';
        messageArgs = {navigationToHospitalOrder: messageUrl};
      }
    });
    this.i18nService.getTranslationByKey(messageKey, messageArgs)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((translation: string) => this.successModalMessage = translation);
  }

  selectIsQuoteApprovalForCheckoutRequired(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.CHECKOUT_QUOTE_APPROVAL_MANDATORY).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.isCheckoutQuoteApprovalMandatory = state;
    });
  }

  /**
   * Method for retrieving rfq_flow feature toggle value.
   * @private
   */
  private _selectIsRfqFlowEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.RFQ_FLOW).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isRfqFlowEnabled => {
      this.isRfqFlowEnabled = isRfqFlowEnabled;
    });
  }
}
